/* eslint-disable no-console */
import { HubConnectionBuilder } from '@microsoft/signalr';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { isToggleEnabled } from 'react-awesome-feature-toggles';
import { useDispatch, useSelector } from 'react-redux';
import { getToken } from 'utils/cops-authenticator';
import { connectionStates } from '../../utils/app-configs';
import { setSocketConnection } from '../containers/socket-actions';

const checkFeatureFlags = () => {
    const featureFlags = [
        'useRealtimeEventsForMessageCenter',
        'useRealtimeEventsForGanttRouteOptimisation',
        'useRealtimeEventsForOrderDetails',
        'useRealtimeEventsForPlanboardGantt',
    ];
    return featureFlags.some(e => isToggleEnabled(e));
};

const maxRetries = 3;

const SocketConnection = ({ children }) => {
    const dispatch = useDispatch();
    const hubConnection = useRef(null);
    const [retryCounter, setRetryCounter] = useState(0);
    const [shouldRetry, setShouldRetry] = useState(false);
    const serviceCenter = useSelector(state => state.serviceCenter.activeServiceCenter);

    const startConnection = async () => {
        if (hubConnection.current) {
            console.warn('[SOCKET] - Connection already active, skipping reconnect attempt...');
            setShouldRetry(false);
            setRetryCounter(0);
            return;
        }
        try {
            dispatch(setSocketConnection({ state: connectionStates.Loading }));
            const baseUrl = window.location.origin.includes('localhost')
                ? 'http://localhost:9000'
                : window.location.origin;

            const connection = new HubConnectionBuilder()
                .withUrl(`${baseUrl}/events`, {
                    accessTokenFactory: () => getToken(),
                })
                .withAutomaticReconnect()
                .build();

            connection.onclose(() => {
                console.warn('[SOCKET] - Socket connection closed');
                dispatch(setSocketConnection({ state: connectionStates.Loading }));
                hubConnection.current = null;
                setShouldRetry(true);
            });

            hubConnection.current = connection;
            await connection.start();
            console.warn(`[SOCKET] - Socket connection started`);
            setShouldRetry(false);
            setRetryCounter(0);
            dispatch(setSocketConnection(connection));
        } catch (err) {
            console.error('[SOCKET] - Error starting socket connection:', err);
            dispatch(setSocketConnection({ state: connectionStates.Error }));
        }
    };

    useEffect(() => {
        if (checkFeatureFlags()) {
            startConnection();
        }
        return () => {
            if (hubConnection.current) {
                // eslint-disable-next-line max-len
                console.warn(`[SOCKET] - Closing connection`);
                hubConnection.current.stop();
                hubConnection.current = null;
            }
        };
    }, [serviceCenter]);

    useEffect(() => {
        if (shouldRetry && retryCounter < maxRetries && hubConnection?.current === null) {
            setRetryCounter((s) => {
                console.warn(`[SOCKET] - Attempting reconnect #${s + 1}`);
                return s + 1;
            });
            startConnection();
        } else if (retryCounter >= maxRetries) {
            console.warn('[SOCKET] - Max retry limit reached, stopping attempts.');
            dispatch(setSocketConnection({ state: connectionStates.Closed }));
            setShouldRetry(false);
            setRetryCounter(0);
        }
    }, [shouldRetry, retryCounter]);

    return <div>{children}</div>;
};

SocketConnection.propTypes = {
    children: PropTypes.node,
};

export default SocketConnection;
